import {
  CREATOR_OUTPUT_KEY,
  LEGACY_CREATOR_OUTPUT_KEY,
} from '../components/Flow/Nodes/Triggers/eventOutputs'

/** "<microservice> - On <Entity> <event>" triggers and "- Wait" actions. */
const EVENT_NODE_NAME_PATTERN = / - (On|Wait) /

export const isEventNodeName = name =>
  typeof name === 'string' && EVENT_NODE_NAME_PATTERN.test(name)

/** Warning attached to the nodes that still use it (shown in the tooltip). */
export const LEGACY_CREATOR_ID_WARNING =
  `"${LEGACY_CREATOR_OUTPUT_KEY}" is deprecated and still in use here. ` +
  `Move to "${CREATOR_OUTPUT_KEY}" (a PassportReference), using its "+" ` +
  `button to extract "passportId" where a string is expected.`

/** Shown when the user tries to draw a new link from the deprecated output. */
export const LEGACY_CREATOR_ID_REFUSED =
  `"${LEGACY_CREATOR_OUTPUT_KEY}" is deprecated and no longer accepts new ` +
  `links. Use "${CREATOR_OUTPUT_KEY}" instead, with its "+" button to extract ` +
  `"passportId" where a string is expected.`

const legacyConnectionsOf = node =>
  isEventNodeName(node?.name)
    ? (node.outputs?.[LEGACY_CREATOR_OUTPUT_KEY]?.connections ?? [])
    : []

/**
 * Lists the event nodes of a serialized diagram that wire the deprecated
 * `creatorId` output.
 *
 * @param {{ nodes?: Record<string, any> }} json the diagram data (editor.toJSON() shape)
 * @returns {Array<{ id: number | string, name: string, connections: number }>}
 */
export const findLegacyCreatorIdConnections = json => {
  const nodes = json?.nodes ?? {}

  return Object.keys(nodes).reduce((legacyNodes, nodeId) => {
    const node = nodes[nodeId]
    const connections = legacyConnectionsOf(node)
    if (connections.length === 0) return legacyNodes

    return [
      ...legacyNodes,
      {
        id: node.id ?? nodeId,
        name: node.name,
        connections: connections.length,
      },
    ]
  }, [])
}

const connectionKey = (sourceId, targetId, inputKey) =>
  `${String(sourceId)}->${String(targetId)}:${String(inputKey)}`

let legacyNodeIds = new Set()
let legacyConnectionKeys = new Set()

export const clearLegacyCreatorIdUsage = () => {
  legacyNodeIds = new Set()
  legacyConnectionKeys = new Set()
}

export const registerLegacyCreatorIdUsage = json => {
  clearLegacyCreatorIdUsage()

  const legacyNodes = findLegacyCreatorIdConnections(json)
  legacyNodes.forEach(({ id }) => legacyNodeIds.add(String(id)))

  const nodes = json?.nodes ?? {}
  Object.keys(nodes).forEach(nodeKey => {
    const node = nodes[nodeKey]
    const sourceId = node?.id ?? nodeKey

    legacyConnectionsOf(node).forEach(({ node: targetId, input }) => {
      legacyConnectionKeys.add(connectionKey(sourceId, targetId, input))
    })
  })

  return { legacyNodes }
}

export const hasDeprecatedCreatorIdOutput = nodeId =>
  legacyNodeIds.has(String(nodeId))

export const shouldRefuseDeprecatedConnection = ({ output, input } = {}) => {
  if (output?.key !== LEGACY_CREATOR_OUTPUT_KEY) return false
  if (!isEventNodeName(output?.node?.name)) return false

  const sourceId = output?.node?.id
  const targetId = input?.node?.id
  if (sourceId === undefined || targetId === undefined) return true

  return !legacyConnectionKeys.has(
    connectionKey(sourceId, targetId, input?.key),
  )
}

export const formatLegacyCreatorIdWarning = legacyNodes => {
  if (legacyNodes === undefined || legacyNodes.length === 0) return ''

  const nodes = legacyNodes
    .map(
      ({ name, connections }) =>
        `"${name}" (${connections} ${connections === 1 ? 'link' : 'links'})`,
    )
    .join(', ')

  return (
    `This diagram still uses the deprecated "${LEGACY_CREATOR_OUTPUT_KEY}" ` +
    `output of event triggers on ${nodes}. It keeps working, but it accepts ` +
    `no new links: move to "${CREATOR_OUTPUT_KEY}" (a PassportReference), ` +
    `using its "+" button to extract "passportId" where a string is expected.`
  )
}

const setNodeWarning = (node, message) => {
  const { [LEGACY_CREATOR_OUTPUT_KEY]: _previous, ...warnings } =
    node.validation?.warnings ?? {}

  node.validation = {
    ...node.validation,
    warnings:
      message === null
        ? warnings
        : { ...warnings, [LEGACY_CREATOR_OUTPUT_KEY]: message },
  }
  if (typeof node.update === 'function') node.update()
}

export const markLegacyCreatorIdNodes = (editor, legacyNodes) => {
  legacyNodes.forEach(({ id }) => {
    const node = editor?.nodes?.find(n => String(n.id) === String(id))
    if (node === undefined) return

    setNodeWarning(node, LEGACY_CREATOR_ID_WARNING)
  })
}

export const refreshDeprecatedCreatorIdWarning = node => {
  const output = node?.outputs?.get?.(LEGACY_CREATOR_OUTPUT_KEY)
  if (output === undefined || !isEventNodeName(node?.name)) return false

  const inUse = output.connections.length > 0
  setNodeWarning(node, inUse ? LEGACY_CREATOR_ID_WARNING : null)

  return inUse
}
